import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import config from '../config';
import { Settings, Layers } from 'lucide-react';
import { getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';

import { calculateVariation, renderVariation as renderVariationBase } from '../utils/variationUtils';
import { bornesDeLaPeriodeChoisie, calculatePreviousPeriod as bornesDeComparaison } from '../utils/periodUtils';
/**
 * Composant pour afficher la ventilation des performances par type de correspondance
 * Utilise l'endpoint /keywords existant et traite les données côté client
 */
const MatchTypeBreakdown = ({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  token,
  onMatchTypeFilter,
  selectedMatchTypeFilter,
  selectedQualityScoreFilter,
  selectedCampaignFilter,
  selectedCpcTierFilter,
  userRole = 'USER',
  compareEnabled = false,
  comparisonType = ''
}) => {
  const [breakdownData, setBreakdownData] = useState(null);
  const [previousBreakdownData, setPreviousBreakdownData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Calcul de la période précédente
  const calculatePreviousPeriod = useCallback(() => {
    if (!comparisonType || !selectedPeriod) return null;

    // Les bornes de la période regardée, puis celles auxquelles on la compare. Les deux
    // calculs vivaient ici, recopiés dans une vingtaine d'écrans : ils sont dans
    // `utils/periodUtils`, seul endroit à savoir ce que « hier » veut dire un jour de
    // changement d'heure.
    const plage = bornesDeLaPeriodeChoisie(selectedPeriod, customStartDate, customEndDate);
    if (!plage) return null;

    return bornesDeComparaison(plage.startDate, plage.endDate, comparisonType);
  }, [comparisonType, selectedPeriod, customStartDate, customEndDate]);


  // Affichage de la variation
  const renderVariation = (variation, isInversed = false) => (compareEnabled ? renderVariationBase(variation, isInversed) : null);

  // Map pour accéder rapidement aux données de la période précédente par matchType
  const previousBreakdownMap = useMemo(() => {
    if (!compareEnabled || !previousBreakdownData?.breakdown) return {};
    const map = {};
    previousBreakdownData.breakdown.forEach(row => {
      map[row.matchType] = row;
    });
    return map;
  }, [compareEnabled, previousBreakdownData?.breakdown]);

  // Configuration des métriques pour ce composant
  const componentMetrics = useComponentMetrics('keywords_match_type', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Fonction pour charger les données des mots-clés et calculer la ventilation
  const loadMatchTypeBreakdown = async () => {
    if (!selectedAccount || !token) return;
    
    try {
      setLoading(true);
      setError(null);
      const requestBody = {
        sources: [selectedAccount.id],
        period: selectedPeriod
      };

      if (selectedPeriod === 'CUSTOM') {
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      const response = await axios.post(
        `${config.API_URL}/api/dashboard/keywords`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const keywordsData = response.data.data[0];
        
        if (keywordsData.keywords && keywordsData.keywords.length > 0) {
          // Calculer la ventilation par type de correspondance
          const breakdown = calculateMatchTypeBreakdown(keywordsData.keywords);
          setBreakdownData(breakdown);
        } else {
          setBreakdownData(null);
        }
      } else {
        setBreakdownData(null);
      }
    } catch (err) {
      console.error('❌ [MATCH-TYPE-BREAKDOWN] Erreur:', err);
      setError('Erreur lors du chargement de la ventilation');
      setBreakdownData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour calculer la ventilation par type de correspondance
  const calculateMatchTypeBreakdown = (keywords) => {
    // Filtrer par campagne d'abord si un filtre est actif
    let filteredKeywords = keywords;
    if (selectedCampaignFilter !== null && selectedCampaignFilter !== undefined) {
      filteredKeywords = keywords.filter(keyword => {
        return keyword.campaignName === selectedCampaignFilter.id;
      });
    }
    
    // Ensuite filtrer par score de qualité si un filtre est actif
    if (selectedQualityScoreFilter !== null && selectedQualityScoreFilter !== undefined) {
      filteredKeywords = filteredKeywords.filter(keyword => {
        const keywordScore = keyword.qualityScore || 0;
        return keywordScore === selectedQualityScoreFilter;
      });
    }
    
    // Enfin filtrer par palier CPC si un filtre est actif
    if (selectedCpcTierFilter !== null && selectedCpcTierFilter !== undefined) {
      filteredKeywords = filteredKeywords.filter(keyword => {
        const cpc = Number(keyword.cpc) || 0;
        return cpc >= selectedCpcTierFilter.min && cpc <= selectedCpcTierFilter.max;
      });
    }

    const matchTypeGroups = {};
    let totalKeywords = 0;

    filteredKeywords.forEach(keyword => {
      const matchType = keyword.matchType || 'Non défini';
      
      if (!matchTypeGroups[matchType]) {
        matchTypeGroups[matchType] = {
          matchType: matchType,
          keywordCount: 0,
          totalImpressions: 0,
          totalClicks: 0,
          totalCost: 0,
          totalConversions: 0,
          totalConversionsValue: 0
        };
      }
      
      const group = matchTypeGroups[matchType];
      group.keywordCount++;
      group.totalImpressions += keyword.impressions || 0;
      group.totalClicks += keyword.clicks || 0;
      group.totalCost += keyword.cost || 0;
      group.totalConversions += keyword.conversions || 0;
      group.totalConversionsValue += keyword.conversionsValue || 0;
      
      totalKeywords++;
    });

    // Convertir en array et calculer les métriques
    const breakdownArray = Object.keys(matchTypeGroups).map(matchTypeKey => {
      const data = matchTypeGroups[matchTypeKey];
      
      const ctr = data.totalImpressions > 0 ? (data.totalClicks / data.totalImpressions * 100) : 0;
      const cpc = data.totalClicks > 0 ? (data.totalCost / data.totalClicks) : 0;
      const conversionRate = data.totalClicks > 0 ? (data.totalConversions / data.totalClicks * 100) : 0;
      const cpa = data.totalConversions > 0 ? (data.totalCost / data.totalConversions) : 0;
      const cos = data.totalConversionsValue > 0 ? (data.totalCost / data.totalConversionsValue * 100) : 0;
      
      return {
        matchType: data.matchType,
        keywordCount: data.keywordCount,
        keywordPercentage: Number(((data.keywordCount / totalKeywords) * 100).toFixed(1)),
        impressions: data.totalImpressions,
        clicks: data.totalClicks,
        cost: data.totalCost,
        conversions: data.totalConversions,
        conversionsValue: data.totalConversionsValue,
        ctr: ctr,
        cpc: cpc,
        conversionRate: conversionRate,
        cpa: cpa,
        cos: cos
      };
    });

    // Calculer les totaux sur les données filtrées
    const totals = filteredKeywords.reduce((acc, keyword) => ({
      keywordCount: acc.keywordCount + 1,
      impressions: acc.impressions + (keyword.impressions || 0),
      clicks: acc.clicks + (keyword.clicks || 0),
      cost: acc.cost + (keyword.cost || 0),
      conversions: acc.conversions + (keyword.conversions || 0),
      conversionsValue: acc.conversionsValue + (keyword.conversionsValue || 0)
    }), { keywordCount: 0, impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
    totals.cpc = totals.clicks > 0 ? (totals.cost / totals.clicks) : 0;
    totals.conversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
    totals.cpa = totals.conversions > 0 ? (totals.cost / totals.conversions) : 0;
    totals.cos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;

    return {
      breakdown: breakdownArray,
      totals: totals,
      totalKeywords: totalKeywords
    };
  };

  // Fonction de tri
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Filtrer et trier les données de ventilation
  const filteredAndSortedBreakdown = React.useMemo(() => {
    if (!breakdownData?.breakdown) return [];
    
    let filtered = breakdownData.breakdown;
    
    // Filtrer par type de correspondance si un filtre est actif
    if (selectedMatchTypeFilter !== null && selectedMatchTypeFilter !== undefined) {
      filtered = filtered.filter(row => row.matchType === selectedMatchTypeFilter);
    }
    
    return [...filtered].sort((a, b) => {
      const aValue = a[sortConfig.key] ?? 0;
      const bValue = b[sortConfig.key] ?? 0;
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [breakdownData, sortConfig, selectedMatchTypeFilter]);

  // Charger les données quand les dépendances changent
  useEffect(() => {
    if (selectedAccount && selectedPeriod && isVisible) {
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }
      loadMatchTypeBreakdown();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, isVisible, selectedQualityScoreFilter, selectedCampaignFilter, selectedCpcTierFilter]);

  // Charger les données de la période précédente pour la comparaison
  useEffect(() => {
    const loadPreviousPeriodData = async () => {
      if (!compareEnabled || !comparisonType || !selectedAccount || !token) {
        setPreviousBreakdownData(null);
        return;
      }

      const previousDates = calculatePreviousPeriod();
      if (!previousDates) {
        setPreviousBreakdownData(null);
        return;
      }

      try {
        const requestBody = {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: previousDates.startDate,
          customEndDate: previousDates.endDate
        };

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/keywords`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success && response.data.data && response.data.data.length > 0) {
          const keywordsData = response.data.data[0];
          if (keywordsData.keywords && keywordsData.keywords.length > 0) {
            const breakdown = calculateMatchTypeBreakdown(keywordsData.keywords);
            setPreviousBreakdownData(breakdown);
          } else {
            setPreviousBreakdownData(null);
          }
        } else {
          setPreviousBreakdownData(null);
        }
      } catch (err) {
        console.error('❌ [MATCH-TYPE-BREAKDOWN] Erreur chargement période précédente:', err);
        setPreviousBreakdownData(null);
      }
    };

    loadPreviousPeriodData();
  }, [compareEnabled, comparisonType, selectedAccount, selectedPeriod, customStartDate, customEndDate, token, selectedQualityScoreFilter, selectedCampaignFilter, selectedCpcTierFilter]);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Layers size={16} />&nbsp;Mots-clés par Type de Correspondance</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          <button
            onClick={onToggleVisibility}
            className="section-toggle-btn"
            title="Masquer/Afficher la section"
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
      </div>

      {!isVisible ? null : (
        <>
          {loading ? (
            <div className="loading-state" style={{ flexDirection: 'column' }}>
              <div className="loading-spinner"></div>
              Calcul de la ventilation...
            </div>
          ) : error ? (
            <div className="error-message">
              {error}
            </div>
          ) : !breakdownData || !breakdownData.breakdown || breakdownData.breakdown.length === 0 ? (
            <div className="empty-message">
              Aucune donnée disponible pour la ventilation par type de correspondance
            </div>
          ) : (
            <>
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('matchType')} className="sortable">
                        Corresp. {sortConfig.key === 'matchType' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('keywordCount')} className="sortable numeric">
                        Nb. kw {sortConfig.key === 'keywordCount' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      {isComponentMetricSelected('impressions') && (
                        <th onClick={() => handleSort('impressions')} className="sortable numeric">
                          Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('ctr') && (
                        <th onClick={() => handleSort('ctr')} className="sortable numeric">
                          CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('clics') && (
                        <th onClick={() => handleSort('clicks')} className="sortable numeric">
                          Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cpc') && (
                        <th onClick={() => handleSort('cpc')} className="sortable numeric">
                          CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cout') && (
                        <th onClick={() => handleSort('cost')} className="sortable numeric">
                          Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('taux_conv') && (
                        <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                          Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('conv') && (
                        <th onClick={() => handleSort('conversions')} className="sortable numeric">
                          Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cpa') && (
                        <th onClick={() => handleSort('cpa')} className="sortable numeric">
                          CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('valeur_conv') && (
                        <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                          Valeur conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cos') && (
                        <th onClick={() => handleSort('cos')} className="sortable numeric">
                          COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedBreakdown.map((row, index) => {
                      const cosClass = getCosClassName(row.cos || 0, selectedAccount?.id || 'global');
                      const prevRow = previousBreakdownMap[row.matchType];

                      return (
                        <tr key={`match-type-${row.matchType}-${index}`}>
                          <td>
                            <span
                              className="match-type-badge match-type-clickable"
                              onClick={() => onMatchTypeFilter && onMatchTypeFilter(row.matchType)}
                              title={`Filtrer les mots-clés de type ${row.matchType}`}
                            >
                              {row.matchType}
                            </span>
                          </td>
                          <td className="numeric">
                            {formatNumber(row.keywordCount)}
                            {compareEnabled && prevRow && renderVariation(calculateVariation(row.keywordCount, prevRow.keywordCount))}
                          </td>
                          {isComponentMetricSelected('impressions') && (
                            <td className="numeric">
                              {formatNumber(row.impressions)}
                              {compareEnabled && prevRow && renderVariation(calculateVariation(row.impressions, prevRow.impressions))}
                            </td>
                          )}
                          {isComponentMetricSelected('ctr') && (
                            <td className="numeric">
                              {formatNumber(row.ctr, 2)}%
                              {compareEnabled && prevRow && renderVariation(calculateVariation(row.ctr, prevRow.ctr))}
                            </td>
                          )}
                          {isComponentMetricSelected('clics') && (
                            <td className="numeric">
                              {formatNumber(row.clicks)}
                              {compareEnabled && prevRow && renderVariation(calculateVariation(row.clicks, prevRow.clicks))}
                            </td>
                          )}
                          {isComponentMetricSelected('cpc') && (
                            <td className="numeric">
                              €{formatNumber(row.cpc, 2)}
                              {compareEnabled && prevRow && renderVariation(calculateVariation(row.cpc, prevRow.cpc), true)}
                            </td>
                          )}
                          {isComponentMetricSelected('cout') && (
                            <td className="numeric">
                              €{formatNumber(row.cost, 2)}
                              {compareEnabled && prevRow && renderVariation(calculateVariation(row.cost, prevRow.cost), true)}
                            </td>
                          )}
                          {isComponentMetricSelected('taux_conv') && (
                            <td className="numeric">
                              {formatNumber(row.conversionRate, 2)}%
                              {compareEnabled && prevRow && renderVariation(calculateVariation(row.conversionRate, prevRow.conversionRate))}
                            </td>
                          )}
                          {isComponentMetricSelected('conv') && (
                            <td className="numeric">
                              {formatNumber(row.conversions, 1)}
                              {compareEnabled && prevRow && renderVariation(calculateVariation(row.conversions, prevRow.conversions))}
                            </td>
                          )}
                          {isComponentMetricSelected('cpa') && (
                            <td className={`numeric ${getCpaClassName(row.cpa, selectedAccount?.id || 'global', 'keywords_match_type')}`}>
                              {row.cpa >= 0 ? `€${formatNumber(row.cpa, 2)}` : '-'}
                              {compareEnabled && prevRow && renderVariation(calculateVariation(row.cpa, prevRow.cpa), true)}
                            </td>
                          )}
                          {isComponentMetricSelected('valeur_conv') && (
                            <td className="numeric">
                              €{formatNumber(row.conversionsValue, 2)}
                              {compareEnabled && prevRow && renderVariation(calculateVariation(row.conversionsValue, prevRow.conversionsValue))}
                            </td>
                          )}
                          {isComponentMetricSelected('cos') && (
                            <td className={`numeric ${getCosClassName(row.cos || 0, selectedAccount?.id || 'global', 'keywords_match_type')}`}>
                              {formatNumber(row.cos, 1)}%
                              {compareEnabled && prevRow && renderVariation(calculateVariation(row.cos, prevRow.cos), true)}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  {!selectedMatchTypeFilter && (
                    <tfoot>
                      {(() => {
                        const totals = breakdownData.totals;
                        const prevTotals = previousBreakdownData?.totals;

                        return (
                          <tr className="totals-row">
                            <td><strong>TOTAL</strong></td>
                            <td className="numeric">
                              <strong>{formatNumber(totals.keywordCount)}</strong>
                              {compareEnabled && prevTotals && renderVariation(calculateVariation(totals.keywordCount, prevTotals.keywordCount))}
                            </td>
                            {isComponentMetricSelected('impressions') && (
                              <td className="numeric">
                                <strong>{formatNumber(totals.impressions)}</strong>
                                {compareEnabled && prevTotals && renderVariation(calculateVariation(totals.impressions, prevTotals.impressions))}
                              </td>
                            )}
                            {isComponentMetricSelected('ctr') && (
                              <td className="numeric">
                                <strong>{formatNumber(totals.ctr, 2)}%</strong>
                                {compareEnabled && prevTotals && renderVariation(calculateVariation(totals.ctr, prevTotals.ctr))}
                              </td>
                            )}
                            {isComponentMetricSelected('clics') && (
                              <td className="numeric">
                                <strong>{formatNumber(totals.clicks)}</strong>
                                {compareEnabled && prevTotals && renderVariation(calculateVariation(totals.clicks, prevTotals.clicks))}
                              </td>
                            )}
                            {isComponentMetricSelected('cpc') && (
                              <td className="numeric">
                                <strong>€{formatNumber(totals.cpc, 2)}</strong>
                                {compareEnabled && prevTotals && renderVariation(calculateVariation(totals.cpc, prevTotals.cpc), true)}
                              </td>
                            )}
                            {isComponentMetricSelected('cout') && (
                              <td className="numeric">
                                <strong>€{formatNumber(totals.cost, 2)}</strong>
                                {compareEnabled && prevTotals && renderVariation(calculateVariation(totals.cost, prevTotals.cost), true)}
                              </td>
                            )}
                            {isComponentMetricSelected('taux_conv') && (
                              <td className="numeric">
                                <strong>{formatNumber(totals.conversionRate, 2)}%</strong>
                                {compareEnabled && prevTotals && renderVariation(calculateVariation(totals.conversionRate, prevTotals.conversionRate))}
                              </td>
                            )}
                            {isComponentMetricSelected('conv') && (
                              <td className="numeric">
                                <strong>{formatNumber(totals.conversions, 1)}</strong>
                                {compareEnabled && prevTotals && renderVariation(calculateVariation(totals.conversions, prevTotals.conversions))}
                              </td>
                            )}
                            {isComponentMetricSelected('cpa') && (
                              <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', 'keywords_match_type')}`}>
                                <strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong>
                                {compareEnabled && prevTotals && renderVariation(calculateVariation(totals.cpa, prevTotals.cpa), true)}
                              </td>
                            )}
                            {isComponentMetricSelected('valeur_conv') && (
                              <td className="numeric">
                                <strong>€{formatNumber(totals.conversionsValue, 2)}</strong>
                                {compareEnabled && prevTotals && renderVariation(calculateVariation(totals.conversionsValue, prevTotals.conversionsValue))}
                              </td>
                            )}
                            {isComponentMetricSelected('cos') && (
                              <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', 'keywords_match_type')}`}>
                                <strong>{formatNumber(totals.cos, 1)}%</strong>
                                {compareEnabled && prevTotals && renderVariation(calculateVariation(totals.cos, prevTotals.cos), true)}
                              </td>
                            )}
                          </tr>
                        );
                      })()}
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="keywords_match_type"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Type de Correspondance"
      />
    </div>
  );
};

export default MatchTypeBreakdown;